import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Elements
import Modal from '../elements/modal';
import AvailableCitiesModal from '../elements/available-cities-modal';
import { cities } from '../elements/cities-data';
import { getLocalItem, getSessionItem } from '../../utils/storage';
import { startingPriceLabel, formatInr, PRICING } from '../../utils/pricing';

// Premium components
import {
  PremiumHero,
  SectionWrapper,
  FaqAccordion,
  FinalCTA,
  StructuredData,
} from '../elements/premium';

const SITE_FROM = startingPriceLabel('site');
const BIKE_FROM = startingPriceLabel('bike');
const CAR_FROM  = startingPriceLabel('car');
const AC_FROM   = startingPriceLabel('ac');
const BIKE_GENERAL = formatInr((PRICING.bike && PRICING.bike.services && PRICING.bike.services.general_service && PRICING.bike.services.general_service.price) || 799);
const CAR_BASIC = formatInr((PRICING.car && PRICING.car.basic_service_from) || 2499);

const SERVICES = [
  { label: 'Bike Service',    price: BIKE_GENERAL, href: '/bike-service-near-me',             desc: 'Regular maintenance, oil change, tune-up' },
  { label: 'Bike Repair',     price: BIKE_FROM,    href: '/bike-repair-near-me',              desc: 'Brake pads, clutch, chain, electrical' },
  { label: 'Car Service',     price: CAR_BASIC,    href: '/car-service-near-me',              desc: 'Periodic service, engine care, inspection' },
  { label: 'Car AC Repair',   price: AC_FROM,      href: '/car-ac-repair-near-me',            desc: 'Gas refill, compressor, cooling fix' },
  { label: 'Car Battery',     price: CAR_FROM,     href: '/car-battery-replacement-near-me',  desc: 'Jump start, testing, replacement' },
];

const FAQS = [
  {
    q: 'How do I find a mechanic near me?',
    a: 'With Ride N Repair, you don\'t need to search for a garage. Book online and a certified mechanic arrives at your doorstep within 15 minutes in 32+ cities across India. Bike service starts at \u20B9450.',
  },
  {
    q: 'Is doorstep mechanic service available in my city?',
    a: 'Ride N Repair currently serves 32+ cities including Delhi NCR, Bengaluru, Mumbai, Pune, Hyderabad, Chennai, Kolkata, Ahmedabad, Jaipur, and many more. Check our city list above for full coverage.',
  },
  {
    q: 'How much does a mechanic near me charge?',
    a: 'With Ride N Repair, bike repair starts at \u20B9450 and bike general service from \u20B9799. Car service starts at \u20B92,499. Pricing is fully transparent \u2014 no hidden charges. You see the exact cost before booking.',
  },
  {
    q: 'How quickly can a mechanic reach me?',
    a: 'Our certified mechanics arrive within 15 minutes of booking confirmation. All mechanics are fully equipped with tools and genuine spare parts to complete the service at your location.',
  },
  {
    q: 'Do you provide warranty on doorstep mechanic service?',
    a: 'Yes, every service comes with a 30-day warranty on parts and labour. We use genuine OEM parts and provide before/after photos plus a digital job card for complete transparency.',
  },
  {
    q: 'Can I get a bike mechanic near me for emergency repairs?',
    a: 'Yes. Ride N Repair handles emergency bike repairs including breakdown assistance, jump start, puncture repair, and towing. Book via our app or call +91 120 361 5050 for immediate help.',
  },
  {
    q: 'What brands does your mechanic near me service cover?',
    a: 'We service all major two-wheeler and four-wheeler brands \u2014 Honda, Hero, Bajaj, TVS, Royal Enfield, Yamaha, Maruti Suzuki, Hyundai, Tata, Mahindra, Kia, Toyota, and 40+ more brands.',
  },
  {
    q: 'Is doorstep service safe and reliable?',
    a: 'Absolutely. All Ride N Repair mechanics are background-verified, uniformed, and trained. You receive real-time updates with photos via WhatsApp during the service. Over 2,00,000 customers trust us.',
  },
];

class MechanicNearMe extends Component {
  componentDidMount() {
    const { setOpenModal } = this.props;
    if (
      getLocalItem('phone_provided') === 'yes' ||
      getSessionItem('phone_ignored') === 'yes' ||
      this.props.user.is_logged
    ) {
      setOpenModal(false);
    }
  }

  render() {
    const {
      isMobileBrowser,
      isMobileApp,
      user,
      setUser,
      openModal,
      setOpenModal,
      citiesModal,
      setCitiesModal,
    } = this.props;

    const schemas = [
      {
        '@type': 'Service',
        name: 'Mechanic Near Me \u2014 Doorstep Bike & Car Service',
        provider: { '@type': 'Organization', name: 'Ride N Repair' },
        description: 'Doorstep bike and car mechanic service in 32+ cities across India. Certified mechanics arrive in 15 minutes. Starting \u20B9450.',
        areaServed: { '@type': 'Country', name: 'India' },
      },
      {
        '@type': 'LocalBusiness',
        name: 'Ride N Repair',
        description: 'Doorstep bike and car service across 32+ cities in India. Certified mechanics, genuine parts, 30-day warranty.',
        url: 'https://www.ridenrepair.com',
        telephone: '+91 120 361 5050',
        image: 'https://www.ridenrepair.com/images/logo.webp',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'IN',
        },
        priceRange: '\u20B9450 - \u20B925,000',
        openingHoursSpecification: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '07:00',
          closes: '21:00',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQS.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ];

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <title>{`Mechanic Near Me — Doorstep Bike & Car Service ${SITE_FROM} | Ride N Repair`}</title>
          <meta
            name="description"
            content={`Find a mechanic near you. Ride N Repair sends certified mechanics to your doorstep in 15 minutes. Bike service from ${BIKE_FROM}, car service from ${CAR_BASIC}. 32+ cities. 30-day warranty.`}
          />
          <meta
            name="keywords"
            content="mechanic near me, bike mechanic near me, car mechanic near me, doorstep mechanic, two wheeler mechanic near me, car service near me, bike service near me, Ride N Repair"
          />
          <link rel="canonical" href="https://www.ridenrepair.com/mechanic-near-me" />
          <meta property="og:title" content={`Mechanic Near Me — Doorstep Bike & Car Service ${SITE_FROM} | Ride N Repair`} />
          <meta property="og:description" content={`Find a mechanic near you. Certified mechanics at your doorstep in 15 minutes. Bike service from ${BIKE_FROM}. 32+ cities.`} />
          <meta property="og:url" content="https://www.ridenrepair.com/mechanic-near-me" />
          <meta property="og:type" content="website" />
        </Helmet>

        <StructuredData data={schemas} />

        {!isMobileApp && openModal && (
          <Modal
            isMobileBrowser={isMobileBrowser}
            isMobileApp={isMobileApp}
            openModal={openModal}
            setOpenModal={setOpenModal}
            user={user}
            setUser={setUser}
          />
        )}

        {citiesModal && (
          <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />
        )}

        <Header
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          user={user}
          setUser={setUser}
        />

        <PremiumHero
          badge="Doorstep Service"
          title="Find a Mechanic Near You — Doorstep Service in 15 Minutes"
          subtitle={`Certified mechanics come to your home or office. Bike repair from ${BIKE_FROM}. 30-day warranty. 2,00,000+ customers served.`}
          isMobileBrowser={isMobileBrowser}
          showCarAnimation
          ctas={[
            { label: `Book Now — ${SITE_FROM} Onwards`, href: '/service', variant: 'primary' },
            { label: 'Call: +91 120 361 5050', href: 'tel:+911203615050', variant: 'secondary' },
          ]}
          trustItems={[
            { value: '2,00,000+', label: 'Customers Served' },
            { value: '4.8\u2605', label: 'Customer Rating' },
            { value: '32+', label: 'Cities in India' },
            { value: '15 min', label: 'Avg Arrival Time' },
          ]}
        />

        {/* CITIES GRID */}
        <SectionWrapper kicker="Our Network" heading="Mechanic Near Me — Available Cities">
          <p className="rr-sub" style={{ marginBottom: 32 }}>
            Doorstep bike and car service in 32+ cities. Select your city to book.
          </p>
          <div className="rr-brand-grid">
            {cities.map(city => (
              <Link key={city.key} to={`/city/${city.key}`} className="rr-brand-card">
                <span className="rr-brand-card__label">{city.name}</span>
                <span className="rr-brand-card__count">Doorstep Service</span>
              </Link>
            ))}
          </div>
        </SectionWrapper>

        {/* SERVICES */}
        <SectionWrapper variant="gray" kicker="Transparent Pricing" heading="Doorstep Mechanic Services & Prices">
          <div className="rr-service-grid">
            {SERVICES.map(svc => (
              <Link key={svc.label} to={svc.href} className="rr-service-card">
                <span className="rr-service-card__label">{svc.label}</span>
                <span className="rr-service-card__desc">{svc.desc}</span>
                <span className="rr-service-card__label" style={{ color: '#e63946', marginTop: 8 }}>
                  From {svc.price}
                </span>
              </Link>
            ))}
          </div>
          <div className="rr-text-center rr-mt-32">
            <Link to="/service" className="rr-cta-primary">Get Exact Price for Your Vehicle</Link>
          </div>
        </SectionWrapper>

        {/* WHY DOORSTEP */}
        <SectionWrapper kicker="Why Doorstep" heading="Why Book a Mechanic Near You with Ride N Repair?">
          <div className="rr-keyword__intro" style={{ maxWidth: 800, margin: '0 auto' }}>
            <p>
              Searching for a <strong>mechanic near me</strong>? With Ride N Repair, you do not need to
              visit a workshop or wait in a queue. Our certified mechanics come to your home, office, or
              any location you choose — fully equipped with tools and genuine spare parts.
            </p>
            <p>
              Every service includes <strong>real-time updates with photos via WhatsApp</strong>, a
              digital job card, and a <strong>30-day warranty on parts and labour</strong>. Whether you
              need a quick bike repair or a full car service, our mechanics handle it at your doorstep
              in 32+ cities across India.
            </p>
            <ul style={{ paddingLeft: '1.5rem', lineHeight: '2' }}>
              <li>Mechanics arrive within <strong>15 minutes</strong></li>
              <li>Bike repair from <strong>{'\u20B9'}450</strong>, bike service from <strong>{'\u20B9'}799</strong></li>
              <li>Car service from <strong>{'\u20B9'}2,499</strong></li>
              <li>Genuine OEM parts only</li>
              <li>Transparent pricing — no hidden charges</li>
              <li>30-day warranty on every service</li>
              <li><strong>2,00,000+</strong> customers served across India</li>
            </ul>
          </div>
        </SectionWrapper>

        {/* FAQs */}
        <SectionWrapper variant="gray" narrow kicker="Common Questions" heading="Mechanic Near Me — FAQ">
          <FaqAccordion faqs={FAQS} withSchema={false} />
        </SectionWrapper>

        <FinalCTA
          heading={<>Need a Mechanic Near You?<br />Book Now — Service in 15 Minutes.</>}
          subtitle={`Doorstep service · Certified mechanics · Starting ${SITE_FROM} · 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          setCitiesModal={setCitiesModal}
        />
      </div>
    );
  }
}

export default MechanicNearMe;
